<script setup lang="ts">
import SidebarIcon from 'frontend/common/layout/sidebar-icon.vue'
import { type ISidebarNavigationItem } from 'frontend/common/layout/types/i-sidebar-navigation-item'
import { computed } from 'vue'
import { type RouteLocationNormalizedLoaded, type RouteLocationRaw, useRoute } from 'vue-router'

import { ColorType } from './types/color-type-enum'

interface Props {
  link: RouteLocationRaw
  title: string
  shouldShowSubScript?: boolean
  iconClasses?: string | string[]
  iconText?: string
  colorType?: ColorType
  isCondensed?: boolean
  subItems?: ISidebarNavigationItem[]
  canBeActiveFn?: (route: RouteLocationNormalizedLoaded) => boolean
}
const props = withDefaults(defineProps<Props>(), {
  shouldShowSubScript: false,
  isCondensed: false,
  subItems: null,
  canBeActiveFn: null,
})

const route = useRoute()

const subScript = computed(() => {
  return props.title
    .split(' ')
    .map(word => word[0])
    .join('')
})

const tooltipOptions = computed(() => {
  if (!props.isCondensed) {
    return
  }
  return { title: props.title, placement: 'right' }
})

const canBeActive = computed(() => {
  if (typeof props.canBeActiveFn === 'function') {
    return props.canBeActiveFn(route)
  }
  return true
})
</script>

<template lang="pug">
.sidebar-item(:class="{ 'is-condensed': isCondensed }")
  router-link.header(
    :class="{ 'without-children': !props.subItems?.length, 'can-be-active': canBeActive }"
    :to="link"
    v-tooltip.options="tooltipOptions"
  )
    SidebarIcon(
      v-if="iconClasses || iconText"
      :colorType="colorType"
      :iconClasses="iconClasses"
      :iconText="iconText"
      :sub-script="shouldShowSubScript ? subScript : null"
    )
    span.title {{ title }}
  .sub-items(v-if="props.subItems?.length && !isCondensed")
    SidebarItem(
      v-for="item in props.subItems"
      :color-type="item.colorType"
      :icon-classes="item.iconClasses"
      :icon-text="item.iconText"
      :is-condensed="isCondensed"
      :key="item.title"
      :link="item.link"
      :should-show-sub-script="item.shouldShowSubScript"
      :title="item.title"
    )
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'frontend_stylesheets/theme-variables.scss' as themeVariables;
@import '../../../frontend_stylesheets/_variables.scss';

.sidebar-item {
  display: grid;
}

.header {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  color: map.get(themeVariables.$colors, onSurface);
  height: 40px;
  padding: 0 16px 0 calc(16px - 3px);
  gap: 12px;
  border-left: 3px solid transparent;
  transition: padding 0.1s ease;
  overflow: hidden;

  &:hover {
    background: map.get(themeVariables.$colors, containerLowest);
  }

  &.is-condensed {
    padding: 0 12px 0 calc(12px - 3px);
  }

  &.can-be-active.router-link-active.without-children,
  &.can-be-active.router-link-exact-active {
    background: map.get(themeVariables.$colors, containerHighest);
    color: map.get(themeVariables.$colors, onSurface);
    border-color: map.get(themeVariables.$colors, primary);
    box-sizing: border-box;
  }
}

.sub-items {
  display: none;

  .sidebar-item .header.router-link-active + & {
    display: grid;
  }

  &:deep(.sidebar-item .header) {
    padding-left: calc(44px + 8px);
  }
}

.nav-item {
  transition: background 0.2s;
  width: 100%;

  &:hover {
    transition: background 0.2s;
    background-color: #e8e8e822;
  }
}

.title {
  transition: opacity 0.1s ease;
  white-space: nowrap;

  .sidebar-item.is-condensed & {
    opacity: 0;
  }
}

.nav-link {
  display: flex;
  align-items: center;
  padding: 0.4rem 0.5rem;
  cursor: pointer;
  transition: color 0.1s ease;
  transition: visibility 0.1s ease;
  color: map.get(themeVariables.$colors, onSurface);

  &.nav-item-dashboard {
    box-shadow: $box-inset-shadow;
    color: lighten($success, 30%);
  }

  &.active {
    background: map.get(themeVariables.$colors, containerHighest);
    color: map.get(themeVariables.$colors, onSurface);

    &:hover {
      color: var(--custom-primary);
    }
  }
  &:hover {
    color: $white;
  }
  span {
    margin-left: 0.4rem;
    visibility: initial;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.icon-wrapper {
  height: 30px;
  min-width: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  border-radius: $border-radius;
  border-width: 1px;
  border-style: solid;
  font-size: 0.7rem;
}
</style>
